import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RedeemExperience } from "@/components/RedeemExperience";
import { REDEEM_PAGE_STRINGS, type RedeemLocaleCode } from "@/lib/redeem-page-strings";

export const Route = createFileRoute("/r/$token")({
  component: ShortRedeemPage,
});

function detectBrowserLocale(): RedeemLocaleCode {
  if (typeof window !== "undefined") {
    const qp = new URLSearchParams(window.location.search).get("lang");
    const key = qp?.slice(0, 2).toLowerCase();
    if (key === "sv" || key === "en") return key;
  }
  if (typeof navigator === "undefined") return "en";
  const lang = (navigator.language || "").toLowerCase();
  if (lang.startsWith("sv")) return "sv";
  return "en";
}

function ShortRedeemPage() {
  const { token } = Route.useParams();
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [locale, setLocale] = useState<RedeemLocaleCode>("en");
  const L = REDEEM_PAGE_STRINGS[locale] ?? REDEEM_PAGE_STRINGS.en;

  useEffect(() => {
    setLocale(detectBrowserLocale());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: rpcErr } = await supabase.rpc("resolve_redeem_token", {
        _token: token,
      });
      if (cancelled) return;
      if (rpcErr || !data) {
        setError(true);
        return;
      }
      setFeedbackId(data as string);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="text-center max-w-sm">
          <span className="text-4xl mb-4 block">😔</span>
          <h1 className="text-xl font-bold mb-2">{L.invalidLinkHeader ?? "Invalid link"}</h1>
          <p className="text-muted-foreground">
            {L.invalidLinkBody ?? "This redemption link is no longer valid."}
          </p>
        </div>
      </div>
    );
  }

  if (!feedbackId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">{L.loading}</p>
      </div>
    );
  }

  return <RedeemExperience feedbackId={feedbackId} token={token} />;
}
